import type { EmployeeDTO } from './employee.dto';
import { z } from 'zod/v3';

export const BACKGROUND_CHECK_MAX_BYTES = 25 * 1024 * 1024;
export const ManualBackgroundCheckSchema = z.object({
  employeeId: z.string().min(1),
  requestId: z.string().uuid(),
  executorName: z.string().trim().min(1).max(200),
  completedDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  editingId: z.string().optional(),
  existingFileIds: z.array(z.string()).max(5).default([]),
  providerCheckId: z.string().optional(),
  notes: z.string().trim().max(2000).optional(),
  confirmed: z.literal(true),
  files: z
    .array(
      z.object({
        name: z.string().min(1).max(255),
        contentType: z.enum(['application/pdf', 'image/png', 'image/jpeg']),
        base64: z
          .string()
          .min(1)
          .max(Math.ceil(BACKGROUND_CHECK_MAX_BYTES / 3) * 4),
      }),
    )
    .max(5),
}).refine((input) => input.files.length + input.existingFileIds.length > 0 || !!input.providerCheckId, { message: 'Provide completion evidence.' });
export type ManualBackgroundCheckInput = z.infer<
  typeof ManualBackgroundCheckSchema
>;

export const BackgroundCheckListSchema = z.object({
  search: z.string().trim().max(200).default(''),
  status: z.enum(['ALL', 'NOT_STARTED', 'PENDING', 'COMPLETED', 'NOT_REQUIRED']).default('ALL'),
  source: z.enum(['ALL', 'manual', 'checkr']).default('ALL'),
  includeInactive: z.boolean().default(false),
  limit: z.number().int().min(1).max(100).default(25),
  offset: z.number().int().min(0).default(0),
});
export type BackgroundCheckListInput = z.infer<
  typeof BackgroundCheckListSchema
>;
export type BackgroundCheckSummary = {
  backgroundCheckStatus: 'NOT_STARTED' | 'PENDING' | 'COMPLETED' | 'NOT_REQUIRED';
  backgroundCheckCompletedAt: Date | null;
};

/** Completion records are evidence of a finished process, never clearance. */
export function summarizeBackgroundChecks(
  checks: ReadonlyArray<{
    status: string;
    completedAt: Date | string | null;
    withdrawnAt?: Date | string | null;
  }>,
): BackgroundCheckSummary {
  checks = checks.filter((c) => !c.withdrawnAt);
  if (checks.some((c) => c.status === 'NOT_REQUIRED'))
    return { backgroundCheckStatus: 'NOT_REQUIRED', backgroundCheckCompletedAt: null };
  const completed = checks
    .filter(
      (c) =>
        c.completedAt && ['COMPLETED', 'CLEAR', 'CONSIDER'].includes(c.status),
    )
    .sort(
      (a, b) =>
        new Date(b.completedAt!).getTime() - new Date(a.completedAt!).getTime(),
    )[0];
  if (completed)
    return {
      backgroundCheckStatus: 'COMPLETED',
      backgroundCheckCompletedAt: new Date(completed.completedAt!),
    };
  // Some providers finish without supplying a completion date. Preserve that
  // lifecycle fact, but leave the date unknown for audit timeliness evaluation.
  if (
    checks.some((c) => ['COMPLETED', 'CLEAR', 'CONSIDER'].includes(c.status))
  ) {
    return {
      backgroundCheckStatus: 'COMPLETED',
      backgroundCheckCompletedAt: null,
    };
  }
  return {
    backgroundCheckStatus: checks.some((c) =>
      ['PENDING', 'SUSPENDED', 'DISPUTE'].includes(c.status),
    )
      ? 'PENDING'
      : 'NOT_STARTED',
    backgroundCheckCompletedAt: null,
  };
}

export type BackgroundCheckPerson = Pick<
  EmployeeDTO,
  'id' | 'firstName' | 'lastName' | 'email'
>;

export const BackgroundCheckSyncResultSchema = z.object({
  synced: z.number().int().nonnegative(),
  linked: z.number().int().nonnegative(),
  unmatched: z.number().int().nonnegative(),
  ambiguous: z.number().int().nonnegative(),
  skipped: z.number().int().nonnegative(),
  failed: z.number().int().nonnegative(),
});
export type BackgroundCheckSyncResult = z.infer<typeof BackgroundCheckSyncResultSchema>;
